// filesDisplay — the files library's entity-display module (COMPONENTS
// §Component taxonomy, the `<entity>Display` idiom): the VIEW-MODEL a file is
// rendered from, the pure READERS every surface shares (its nature · what it
// shows · whether it needs review), and the CELLS — a media tile, a document
// row, a voice-note row, the marks a tile earns — that the shelf, the flow, the
// docked panel and the product peek render IDENTICALLY (FRONTEND-PATTERNS
// §Files: one composite, many anchors; a lens never changes the cell).
//
// The view-model MIRRORS the ruled wire (ADR-068 §2: subjects with their source
// · confidence · confirmed / rejected marks; topics; the review stamp; the
// visit and the position mark) so the build's container maps `AnchoredFile` +
// the genesis fields onto it field for field — the schema stays the landed
// truth until then. Props-only; strings through t('files').

import { createElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { INLINE_FAMILIES, SNIFFED_TYPES, type ContentFamily } from '@ems-app/schemas/files/vocabulary';
import type { CatalogArtifactItem } from '@ems-app/schemas/catalog/responses';
import type { FileHiddenSubject } from '@ems-app/schemas/files/responses';
import { DEFAULT_REVIEW_POLICY, isConfirmedSubject, liveSubjects as liveOf, needsReview, reviewReasonsOf, type ReviewPolicy, type ReviewReason, type SubjectSource } from '@ems-app/schemas/files/review';
import { moveRoadsFor } from '@ems-app/schemas/files/moves';
import { Tag } from '../ui/Tag.js';
import { icons, type Icon } from '../ui/icons.js';
import { catalogFileId } from '../../lib/catalogFileId.js';
import { formatBytes, formatDate, formatNumber } from '../../lib/format.js';
import { EntityRef } from './EntityRef.js';
import { refWords } from './assetsDisplay.js';

/** The on-stage control recipe — an `IconButton` over the dark stage ground
 *  (the surface's chrome, a tile's hover acts): one home, never re-inlined. */
// ── the view-model ──────────────────────────────────────────────────────────

/** A described thing (media — no folder) or a filed thing (a document — one folder). Never stored: read from the folder (ADR-068 §2). */
export type FileNature = 'media' | 'document';
/** How a subject link was made — a person, a census count, a category-only answer derived on a one-asset site, a machine read. */
/** The sources ladder (DOMAIN-RULES §7), most trusted first: a person's pick ·
 *  a REQUEST (the photo was taken for a ticket or checklist step that names the
 *  subject) · the count (one photo per counted asset) · derivation (a category
 *  on a site with exactly one asset of it) · a machine (a proposal until a
 *  human confirms it, or the project's trust dial for that source accepts it). */
export type { SubjectSource, ReviewPolicy, ReviewReason };
export { DEFAULT_REVIEW_POLICY, needsReview, reviewReasonsOf };
export type AnchorKind = 'site' | 'asset' | 'model' | 'user' | 'project' | 'ticket';

export interface LibraryPerson {
  id: string;
  label: string;
}

/** What a capture is ABOUT (0..n): an asset_files row with relation = subject. */
export interface LibrarySubject {
  asset_id: string;
  label: string;
  source: SubjectSource;
  /** 0..1 for a machine / derived proposal; null for a human link. */
  confidence: number | null;
  /** How the machine knew ("serial read") — printed beside a proposal, never as a fact. */
  via: string | null;
  confirmed_at: string | null;
  /** Accepted by the project's trust dial for this source, not by a person — printed as such, never as a human's word. */
  auto_accepted?: boolean;
  /** A dismissed proposal is KEPT and marked so it is never re-proposed. */
  rejected_at: string | null;
  /** The asset's category — the ladder: a named asset gives its topic for free (the server joins it; the title reads it). */
  category?: string | null;
  /** Born on this asset — the anchor as a synthetic confirmed human naming (the
   *  read's shape writes it, never a stored row): a plain named row in Shows,
   *  locked out of the picker, never sent back by a classification. */
  born?: boolean;
}

/** A subject outside the viewer's reach — a unit since moved where they cannot
 *  see: its kind alone ("a unit"), never its name or id (the naming rule). */
export type LibraryHiddenSubject = FileHiddenSubject;

export interface FileNote {
  id: string;
  /** `note` = added in the app (`file_notes`) · `transcript` = a voice note's transcript (the extracts seam, machine-made, read-only). The technician's words at capture are the file's `caption` (the journal entry's text), pinned first in the section — never a row here. */
  kind: 'note' | 'transcript';
  text: string;
  author: LibraryPerson | null;
  created_at: string;
  /** Set when the author changed it later — printed as "edited". */
  edited_at?: string | null;
}


export interface LibraryVisit {
  id: string;
  /** The visit's day (ISO date) — the group label + the Facts line. */
  day: string;
  person: LibraryPerson;
}

/** The stage's source for the OPEN file — the host's per-open mint (`useStageLink`):
 *  `src` null while it mints (or for a glyph-only kind); `expiresAt` the client-side
 *  moment (ms) the link goes dead for a NEW load — the stage never requests a link
 *  past it (a frame reports no error, so a dead link would be a silent blank);
 *  `failed` after the mint refused or the bytes refused twice — the card says so. */
export interface StagePreview {
  src: string | null;
  expiresAt: number | null;
  failed: boolean;
}
/** Nothing minted yet (a story's stage, a lens with nothing open). */
export const STAGE_PREVIEW_MINTING: StagePreview = { src: null, expiresAt: null, failed: false };

export interface LibraryFile {
  /** The row stamp the read carried (ADR-084 §3) — a rename / caption sends it back; a catalog copy (no files row) has none. */
  row_version?: number;
  id: string;
  family: ContentFamily;
  content_type: string;
  size_bytes: number;
  original_name: string;
  display_name: string | null;
  previewable: boolean;
  /** The physical facts, producer-made (the browser reads them before the put; the bot's API sends them). No page count — it needs a PDF parser (the PDF-library decision). */
  media: { width?: number; height?: number; duration_ms?: number } | null;
  /** The tile's picture — a resolved URL (the live tab's signed thumb link; the story's swatch). Null = the family glyph. */
  thumb: string | null;
  /** One folder = a document; null = a described thing (the nature rule). */
  folder: string | null;
  /** `subject` = shown here through a subject link (an asset's lens over the site's captures). */
  relation: 'anchor' | 'subject';
  /** A catalog copy (a product's datasheet) — preview + download only, "from the catalog". */
  provenance: 'catalog' | null;
  /** Sits in a restricted folder (the row wears the lock — visible only with the facet key). */
  restricted: boolean;
  uploaded_by: LibraryPerson | null;
  /** The moment (UTC ISO) — rendered in the anchor's zone (site time). */
  captured_at: string;
  subjects: LibrarySubject[];
  /** The subjects the viewer may not name — each read as its kind alone. Absent = none. */
  hidden_subjects?: LibraryHiddenSubject[];
  /** A category word from the register's charter or the short tail (safety · civil · access · damage · general). */
  topics: string[];
  caption: string | null;
  reviewed_at: string | null;
  reviewed_by: LibraryPerson | null;
  visit: LibraryVisit | null;
  /** A field capture's check-in state at the moment (null = not a capture). A fix rides the check-in — the bot has a position only while the technician is checked in. */
  checked_in: boolean | null;
  /** The capture's fix against the site's registered location — a FACT, never a
   *  judgment: `match` within the site's tolerance, `distance_m` from it. Null =
   *  no fix (the usual without a check-in; the phone's failure with one). */
  position: { match: boolean; distance_m: number | null } | null;
  /** Why it waits for a human (empty = nothing to review) — see `reviewReasonsOf`. */
  review_reasons: ReviewReason[];
  /** Committed in this session — the "just now" mark. */
  fresh: boolean;
  deleted_at: string | null;
  /** The bin's facts — who deleted it and when the sweeper lets go of it (bin reads only). */
  deleted_by?: LibraryPerson | null;
  purge_at?: string | null;
  /** The entity it was born on (owns the gate; "Open at the site / asset"). */
  anchor: { kind: AnchorKind; id: string; label: string };
  /** Replace-with-a-new-version: the current version number + how many older ones exist. */
  version: number;
  older_versions: number;
  /** The older versions themselves (newest first) — each a file of its own, read-only in the surface. */
  versions?: LibraryFile[];
  /** On an older version: the current one's id. */
  superseded_by?: string | null;
  /** The Notes section: the technician's words from the capture first, then what people add, a voice note's transcript when it exists. */
  notes?: FileNote[];
  /** On a GROUPED flow: the key of the divider this row sits under (the day · month · year it was taken, in the
   *  screen's zone) — the server's stamp, the same expression that counted the dividers (ADR-078 §4). */
  band?: string;
}

// ── catalog artifacts as view-model rows (ADR-068 §1: sha-addressed, no files
// rows — the content address is the identity; FilePanel's `provenance:
// 'catalog'` face renders them preview + download only) ──────────────────────

export { CATALOG_FILE_PREFIX, catalogFileId, catalogShaOf, isCatalogFileId } from '../../lib/catalogFileId.js';

/** The artifact's family, from its declared mime (display; serving trusts the pack-sniffed type). */
export function familyOfMime(mime: string): ContentFamily {
  return SNIFFED_TYPES[mime] ?? (mime.startsWith('image/') ? 'image' : mime.startsWith('video/') ? 'video' : mime.startsWith('audio/') ? 'audio' : 'document');
}

/** The download filename: the title + the mime's extension (the title is prose). */
export function artifactFilename(a: Pick<CatalogArtifactItem, 'title' | 'mime'>): string {
  const ext = { 'application/pdf': '.pdf', 'text/plain': '.txt', 'image/webp': '.webp', 'image/png': '.png', 'image/jpeg': '.jpg' }[a.mime] ?? '';
  return `${a.title}${ext}`;
}

const ARTIFACT_KIND_FOLDER: Record<string, string> = { datasheet: 'datasheets', manual: 'manuals', certificate: 'certificates', drawing: 'drawings' };

/** A catalog artifact as the surface's synthetic row (the Workbench fixtures' shape, live). */
export function catalogLibraryFile(a: CatalogArtifactItem, model: { id: string; name: string }): LibraryFile {
  const family = familyOfMime(a.mime);
  return {
    // The citing model rides the id — two products sharing one blob (a common
    // brochure) stay two distinct rows (open/walk resolve by id).
    id: catalogFileId(a.sha256, model.id),
    family,
    content_type: a.mime,
    size_bytes: a.size,
    original_name: artifactFilename(a),
    display_name: a.title,
    previewable: a.available && INLINE_FAMILIES.includes(family),
    media: null,
    thumb: null,
    folder: ARTIFACT_KIND_FOLDER[a.kind] ?? 'other',
    relation: 'anchor',
    provenance: 'catalog',
    restricted: false,
    uploaded_by: null,
    captured_at: '',
    subjects: [],
    topics: [],
    caption: null,
    reviewed_at: null,
    reviewed_by: null,
    visit: null,
    checked_in: null,
    position: null,
    review_reasons: [],
    fresh: false,
    deleted_at: null,
    anchor: { kind: 'model', id: model.id, label: model.name },
    version: 1,
    older_versions: 0,
  };
}

// ── the readers (pure; one home so the tile, the row, the panel and the inbox count agree) ──

export function natureOf(file: Pick<LibraryFile, 'folder'>): FileNature {
  return file.folder === null ? 'media' : 'document';
}

/** The restore right per file (ADR-068 §5): the anchor's librarian on any, the
 *  deleter on their own. ONE home — every container maps its keys through this,
 *  never a copy per surface. */
export function canRestoreFile(file: Pick<LibraryFile, 'deleted_by'>, rights: { canManage: boolean; meId: string | null | undefined }): boolean {
  return rights.canManage || (!!rights.meId && file.deleted_by?.id === rights.meId);
}

export function displayName(file: Pick<LibraryFile, 'display_name' | 'original_name'>): string {
  return file.display_name ?? file.original_name;
}

/** A file name inside a SENTENCE — a dialog title, a toast, the rename note —
 *  shortened in the middle so the extension and the tail survive (the file
 *  manager idiom: "422319898_76400…43_n.jpg"); 24 keeps "Delete …?" on one
 *  line of the narrow confirm card. A cell truncates by CSS instead; the
 *  panel's File name fact is the one full reading. */
export const SHORT_NAME_MAX = 24;
export function shortFileName(name: string, max: number = SHORT_NAME_MAX): string {
  if (name.length <= max) return name;
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 && name.length - dot <= 8 ? name.slice(dot) : '';
  const stem = ext ? name.slice(0, dot) : name;
  const tail = stem.slice(-4);
  const budget = max - tail.length - ext.length - 1;
  // No room for a head of four: the plain end cut, within max.
  if (budget < 4) return `${name.slice(0, Math.max(1, max - 1))}\u2026`;
  return `${stem.slice(0, budget)}\u2026${tail}${ext}`;
}

export function isConfirmed(subject: LibrarySubject): boolean {
  return isConfirmedSubject(subject);
}

/** Subjects still standing — a dismissed proposal is out. */
export function liveSubjects(file: Pick<LibraryFile, 'subjects'>): LibrarySubject[] {
  return liveOf(file.subjects);
}

export function namedSubjects(file: Pick<LibraryFile, 'subjects'>): LibrarySubject[] {
  return liveSubjects(file).filter(isConfirmed);
}

/** The named subjects the viewer may not name — standing and confirmed, each "a unit". */
export function hiddenSubjectsOf(file: Pick<LibraryFile, 'hidden_subjects'>): LibraryHiddenSubject[] {
  return liveOf(file.hidden_subjects ?? []).filter(isConfirmedSubject);
}

/** The open proposal (derived or machine, unconfirmed, undismissed) — at most one is offered at a time. */
export function proposalOf(file: Pick<LibraryFile, 'subjects'>): LibrarySubject | null {
  return liveSubjects(file).find((s) => !isConfirmed(s)) ?? null;
}


/** "40 m" · "2.3 km" — the distance a position line prints. */
export function formatDistance(m: number, t: (key: string, o: Record<string, unknown>) => string): string {
  return m < 1000 ? t('panel.metres', { value: formatNumber(Math.round(m)) }) : t('panel.km', { value: formatNumber(m / 1000, { maximumFractionDigits: 1 }) });
}

export type Shows =
  | { kind: 'asset'; subjects: LibrarySubject[]; hidden: LibraryHiddenSubject[]; suggested: false }
  | { kind: 'asset'; subjects: [LibrarySubject]; suggested: true }
  | { kind: 'topics'; topics: string[] }
  | { kind: 'none' };

/** What a tile says on its first line: a named asset (one beyond the viewer's reach as "a unit") · a dashed suggestion · topics · nothing. A guess is never printed as a fact. */
export function showsOf(file: Pick<LibraryFile, 'subjects' | 'hidden_subjects' | 'topics'>): Shows {
  const named = namedSubjects(file);
  const hidden = hiddenSubjectsOf(file);
  if (named.length > 0 || hidden.length > 0) return { kind: 'asset', subjects: named, hidden, suggested: false };
  const proposal = proposalOf(file);
  if (proposal) return { kind: 'asset', subjects: [proposal], suggested: true };
  if (file.topics.length > 0) return { kind: 'topics', topics: file.topics };
  return { kind: 'none' };
}

export function formatDuration(ms: number | undefined): string {
  if (ms === undefined || !Number.isFinite(ms)) return '';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** The glyph that names a file's TYPE — the document-library feel: PDF · Word ·
 *  Excel · PowerPoint · CSV · text · drawing · archive, and the media families.
 *  The family alone is coarse (every office file is `document`), so the sniffed
 *  content type refines it; anything unknown keeps the plain file. Registry
 *  concepts only (ADR-042), monochrome. */
export function fileGlyphOf(family: ContentFamily, contentType?: string): Icon {
  switch (family) {
    case 'image':
      return icons.image;
    case 'video':
      return icons.video;
    case 'audio':
      return icons.audio;
    case 'pdf':
      return icons.filePdf;
    case 'cad':
      return icons.fileCad;
    case 'archive':
      return icons.fileArchive;
    case 'text':
      return contentType === 'text/csv' ? icons.fileCsv : icons.fileText;
    case 'document':
      if (contentType?.includes('wordprocessingml')) return icons.fileWord;
      if (contentType?.includes('spreadsheetml')) return icons.fileExcel;
      if (contentType?.includes('presentationml')) return icons.filePowerpoint;
      return icons.file;
    default:
      return icons.file;
  }
}

/** The file's type glyph — the tile fallback, the row's leading mark, the stage's card. */
export function FamilyGlyph({ family, contentType, size = 20, className = '' }: { family: ContentFamily; contentType?: string; size?: number; className?: string }) {
  // createElement over a registry reference (the compiler's static-components rule
  // reads a capitalized local as a component made during render — it is not).
  return createElement(fileGlyphOf(family, contentType), { size, 'aria-hidden': true, className });
}

// ── the topic words: a charter category (the assets vocabulary) or the tail (files.json) ──
// ── COLLECTIONS — the media grouped on the ladder (DOMAIN-RULES §7: a topic is
//    a category-level word; a named asset gives its category for free) ──
export type CollectionBy = 'asset' | 'topic';
export interface FileCollection {
  /** `asset:<id>` · `topic:<code>` · `none` (the dummy — always last). */
  key: string;
  by: CollectionBy;
  /** The filter value that opens it: an asset id, a topic code, or `none`. */
  value: string;
  label: string;
  count: number;
  /** The newest file — the cover. */
  cover: LibraryFile;
  latest_at: string;
}

/** The pure fold: every media file under EACH of its subjects — by asset, each
 *  named asset (confirmed or counted, never a bare proposal); by topic, its topics
 *  ∪ the category of each named asset. A file with none lands in the dummy.
 *  Order: newest activity first (oldest with `sort`), the dummy last. The live
 *  container reads the same shape from the server over the whole question. */
export function collectionsOf(
  files: LibraryFile[],
  by: CollectionBy,
  opts: { categoryOf: (assetId: string) => string | null; topicLabel: (code: string) => string; noneLabel: string; sort?: 'newest' | 'oldest' },
): FileCollection[] {
  const map = new Map<string, FileCollection>();
  for (const f of files) {
    if (natureOf(f) !== 'media') continue;
    const named = namedSubjects(f);
    const members: { value: string; label: string }[] =
      by === 'asset'
        ? named.map((s) => ({ value: s.asset_id, label: s.label }))
        : [...new Set([...f.topics, ...named.map((s) => opts.categoryOf(s.asset_id)).filter((c): c is string => c !== null)])].map((c) => ({ value: c, label: opts.topicLabel(c) }));
    const targets = members.length > 0 ? members : [{ value: 'none', label: opts.noneLabel }];
    for (const m of targets) {
      const key = m.value === 'none' ? 'none' : `${by}:${m.value}`;
      const cur = map.get(key);
      if (!cur) map.set(key, { key, by, value: m.value, label: m.label, count: 1, cover: f, latest_at: f.captured_at });
      else {
        cur.count += 1;
        if (f.captured_at > cur.latest_at) {
          cur.cover = f;
          cur.latest_at = f.captured_at;
        }
      }
    }
  }
  const newest = (opts.sort ?? 'newest') === 'newest';
  // Ties on the date break by name, so the order never depends on arrival.
  return [...map.values()].sort((a, b) => {
    if (a.value === 'none' || b.value === 'none') return a.value === 'none' ? 1 : -1;
    const byDate = newest ? b.latest_at.localeCompare(a.latest_at) : a.latest_at.localeCompare(b.latest_at);
    return byDate !== 0 ? byDate : a.label.localeCompare(b.label);
  });
}

/** The view-transition name the cover and the open collection's first tile share (a CSS ident). */
export function collectionTransitionName(key: string): string {
  return `fc-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

export interface CollectionTileProps {
  collection: FileCollection;
  /** A tap opens the collection (the composite applies its subject filter). */
  onOpen: (collection: FileCollection) => void;
  timeZone?: string;
  className?: string;
}

/** A STACK: the newest picture on top, two cards peeking behind it, the count in
 *  the corner, the subject's name and the latest date under it. */
export function CollectionTile({ collection: c, onOpen, timeZone, className = '' }: CollectionTileProps) {
  const { t } = useTranslation('files');
  const date = formatDate(c.latest_at, 'date', timeZone ? { timeZone } : {});
  return (
    <button type="button" onClick={() => onOpen(c)} aria-label={`${t('collections.open', { name: c.label })} · ${t('flow.fileCount', { count: c.count })}`} className={`group flex w-full flex-col gap-1.5 text-start outline-none ${className}`}>
      {/* The stack: two offset cards behind the cover (never rotated — the grid stays square). */}
      <span className="relative block aspect-square w-full pb-2 pe-2">
        <span aria-hidden className="absolute inset-0 translate-x-2 translate-y-2 rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-surface-alt)] rtl:-translate-x-2" />
        <span aria-hidden className="absolute inset-0 translate-x-1 translate-y-1 rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-surface)] rtl:-translate-x-1" />
        <span
          className="relative block h-full w-full overflow-hidden rounded-lg bg-[var(--color-bg-stage)] text-[var(--color-on-stage-muted)] ring-1 ring-[var(--color-divider)] transition-transform group-hover:-translate-y-0.5 group-focus-visible:ring-2 group-focus-visible:ring-[var(--color-primary-ring)]"
          style={{ viewTransitionName: collectionTransitionName(c.key) }}
        >
          {c.cover.thumb ? (
            <img src={c.cover.thumb} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-center justify-center">
              <FamilyGlyph family={c.cover.family} contentType={c.cover.content_type} size={40} />
            </span>
          )}
          <span className="absolute bottom-1.5 end-1.5 rounded-full bg-[var(--color-bg-stage)]/75 px-1.5 text-xs font-medium tabular-nums text-[var(--color-on-stage)]">{formatNumber(c.count)}</span>
        </span>
      </span>
      <span className="block min-w-0">
        <span className="block truncate text-sm font-medium text-[var(--color-text-primary)]">{c.label}</span>
        <span className="block truncate text-xs text-[var(--color-text-secondary)]">
          {t('flow.fileCount', { count: c.count })}
          {date && ` · ${t('collections.latest', { date })}`}
        </span>
      </span>
    </button>
  );
}

/** The identity a surface titles a file by: a capture by its CLASSIFICATION —
 *  the named assets, else its topics (the caption is the technician's note, the
 *  phone's file name metadata); a document by its display name. */
/** The title holds the classification while it stays small: the named assets,
 *  then the topics no named asset already implies (the ladder — an asset gives
 *  its category for free), up to three items, then "+n" (the section holds the
 *  rest). An undescribed capture is titled by its moment, as a photo library
 *  names an untitled shot; an unclassified voice note by its word. */
export const TITLE_ITEMS = 3;
export function fileTitle(file: LibraryFile, topicLabel: (topic: string) => string, opts: { voiceNote?: string; moment?: string } = {}): string {
  if (natureOf(file) !== 'media') return displayName(file);
  const named = namedSubjects(file);
  const covered = new Set(named.map((s) => s.category).filter((c): c is string => Boolean(c)));
  const items = [...named.map((s) => s.label), ...file.topics.filter((code) => !covered.has(code)).map(topicLabel)];
  if (items.length > TITLE_ITEMS) return `${items.slice(0, TITLE_ITEMS).join(' · ')} +${items.length - TITLE_ITEMS}`;
  if (items.length > 0) return items.join(' · ');
  if (file.family === 'audio' && opts.voiceNote) return opts.voiceNote;
  if (opts.moment) return opts.moment;
  return displayName(file);
}

export function useTopicLabel(): (topic: string) => string {
  const { t } = useTranslation('files');
  const { t: ta } = useTranslation('assets');
  return (topic) => t(`topics.${topic}`, { defaultValue: ta(`category.${topic}`, { defaultValue: topic }) });
}

// ── the marks a tile earns — overlays on the picture, so they sit on a scrim ──
export type TileMarkTone = 'new' | 'suggested' | 'scrim';

export function TileMark({ tone = 'scrim', children, className = '' }: { tone?: TileMarkTone; children: ReactNode; className?: string }) {
  const toneClass =
    tone === 'new'
      ? 'bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)]'
      : tone === 'suggested'
        ? 'border border-dashed border-[var(--color-warning)] bg-[var(--color-bg-stage)]/70 text-[var(--color-on-stage)]'
        : 'bg-[var(--color-bg-stage)]/60 text-[var(--color-on-stage)]';
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-px text-[0.65rem] font-semibold leading-4 ${toneClass} ${className}`}>
      {children}
    </span>
  );
}

// ── the shared "what it shows" line: the named assets AND the topics together;
//    a proposal is the name alone in the dashed warning style (the mark on the
//    picture and the panel already say "suggested" — the word would only eat
//    the width; screen readers still hear it) ──
export function ShowsLine({ file, className = '' }: { file: LibraryFile; className?: string }) {
  const { t } = useTranslation('files');
  const { t: ta } = useTranslation('assets');
  const topicLabel = useTopicLabel();
  const shows = showsOf(file);
  const topics = file.topics.map(topicLabel).join(', ');
  if (shows.kind === 'none') {
    return <span className={`text-[var(--color-text-disabled)] ${className}`}>{t('tile.notClassified')}</span>;
  }
  if (shows.kind === 'topics') {
    return (
      <span className={`font-medium text-[var(--color-text-primary)] ${className}`} title={topics}>
        {topics}
      </span>
    );
  }
  if (shows.suggested) {
    const name = shows.subjects[0].label;
    return (
      <span className={`font-medium text-[var(--color-warning-bold)] dark:text-[var(--color-warning)] ${className}`} title={t('tile.suggested', { name })}>
        <span className="sr-only">{t('review.suggested')} </span>
        <span className="border-b border-dashed border-current">{name}</span>
        {topics && <span className="text-[var(--color-text-secondary)]"> · {topics}</span>}
      </span>
    );
  }
  const names = [...shows.subjects.map((s) => s.label), ...shows.hidden.map((h) => refWords(h, ta))].join(', ');
  const full = topics ? `${names} · ${topics}` : names;
  return (
    <span className={`font-medium text-[var(--color-text-primary)] ${className}`} title={full}>
      {names}
      {topics && <span className="font-normal text-[var(--color-text-secondary)]"> · {topics}</span>}
    </span>
  );
}

/** The tile's who · when, sized to what the divider already says:
 *  ungrouped → the day and month (+ the year when not this year); by day → the
 *  time; by month → the day + the time; by year → the day and month. Never
 *  truncated — short by construction; the full timestamp rides `title`. */
export type CaptionContext = 'none' | 'day' | 'month' | 'year';
export function captionWhen(iso: string, context: CaptionContext, timeZone?: string): string {
  const tz = timeZone ? { timeZone } : {};
  const yearIn = (d: Date) => new Intl.DateTimeFormat('en', { ...tz, year: 'numeric' }).format(d);
  const thisYear = yearIn(new Date(iso)) === yearIn(new Date());
  switch (context) {
    case 'day':
      return formatDate(iso, 'time', tz) ?? '';
    case 'month':
      return [formatDate(iso, 'dayMonth', tz), formatDate(iso, 'time', tz)].filter(Boolean).join(' · ');
    case 'year':
      return formatDate(iso, 'dayMonth', tz) ?? '';
    default:
      // ungrouped: the day and month, the year only when it is not this year
      return formatDate(iso, thisYear ? 'dayMonth' : 'dayMonthYear', tz) ?? '';
  }
}

// ── a media TILE (image · video · voice note) ────────────────────────────────
// ONE tile for every described thing, so a voice note keeps its place in the
// day's sequence and wears the same marks and caption; its face is the stage
// ground with the mic and the duration (there is no picture to show).
export interface FileTileProps {
  file: LibraryFile;
  selected?: boolean;
  /** A click SELECTS — the docked panel shows the file (the master-detail law; no ⋯ on a tile). */
  onSelect?: (file: LibraryFile) => void;
  /** Shown through a subject link (an asset's lens) — the "from the site visit" mark. */
  lens?: boolean;
  /** Visible quick acts (download · classify) — top end, revealed on hover / focus. */
  quickActions?: ReactNode;
  /** The anchor's zone for the caption's time (site time). */
  timeZone?: string;
  /** What the surrounding group header already says — the caption's date shrinks to the rest. */
  context?: CaptionContext;
  className?: string;
}

/** The keyboard walk's contract with the cells (`rovingFocus`): the tile's own
 *  control, the row's control, the quick acts that leave the tab order, and the
 *  file id a closing surface returns focus to. Named HERE, beside the markup, so
 *  the two cannot drift apart. */
export const WALK = {
  /** The tile's open control (a `[data-tile]` button inside a `[data-file-id]` wrapper). */
  tile: '[data-tile]',
  /** The row's name control (the `EntityRef` button inside the `[data-row-control]` span). */
  row: '[data-row-control] > button',
  /** The quick acts' container — its buttons leave the tab order. */
  quick: '[data-quick]',
} as const;

export function FileTile({ file, selected = false, onSelect, lens = false, quickActions, timeZone, context = 'none', className = '' }: FileTileProps) {
  const { t } = useTranslation('files');
  const isNote = file.family === 'audio';
  const name = isNote ? t('tile.voiceNote', { duration: formatDuration(file.media?.duration_ms) }) : displayName(file);
  const review = needsReview(file);
  const when = captionWhen(file.captured_at, context, timeZone);
  const whenFull = formatDate(file.captured_at, 'datetime', timeZone ? { timeZone } : {}) ?? '';
  const who = file.uploaded_by?.label;
  return (
    // @container: a narrow tile (a phone's three columns) drops the who · when
    // line by its OWN width — the group header or the surface carries it.
    <div data-file-id={file.id} className={`group relative flex min-w-0 flex-col gap-1 @container ${className}`}>
      {/* The tile's own open control — a composite-internal control like a
          DataTable row: a bespoke frame the Button primitive would not fit. */}
      <button
        type="button"
        data-tile
        aria-label={t('actions.open', { name })}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onSelect?.(file)}
        className={
          'relative aspect-square w-full overflow-hidden rounded-md bg-[var(--color-bg-surface-alt)] outline-none focus-ring ' +
          (selected ? 'ring-2 ring-[var(--color-primary-ring)] ring-offset-2 ring-offset-[var(--color-canvas)]' : '')
        }
      >
        {isNote ? (
          // The audio face: the stage ground, the mic — a sound has no picture.
          <span className="flex h-full w-full items-center justify-center bg-[var(--color-bg-stage)] text-[var(--color-on-stage-muted)]">
            <icons.audio size={40} aria-hidden />
          </span>
        ) : file.thumb ? (
          <img src={file.thumb} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-[var(--color-text-disabled)]">
            <FamilyGlyph family={file.family} size={32} />
          </span>
        )}
        {(file.family === 'video' || isNote) && (
          <span aria-hidden className="absolute inset-0 flex items-center justify-center text-[var(--color-on-stage)] drop-shadow">
            <icons.play size={28} weight="fill" />
          </span>
        )}
        {/* The picture's three corners — every mark stays ON the picture:
            top start the state marks · bottom start the position pin ·
            bottom end the duration or the ×n count. Decorative: the caption
            and the sr text carry the words. */}
        <span aria-hidden className="pointer-events-none absolute start-1.5 top-1.5 flex flex-wrap gap-1">
          {file.fresh && <TileMark tone="new">{t('marks.new')}</TileMark>}
          {lens && file.relation === 'subject' && <TileMark>{t('marks.lens')}</TileMark>}
          {review && <TileMark tone="suggested">{t('marks.toReview')}</TileMark>}
        </span>
        {/* The position pin: green when the recorded position matches the site;
            WARNING when it does not (a capture taken somewhere else — a GPS
            fault or a claim to check); absent when not recorded. */}
        {file.position && (
          <span
            aria-hidden
            className={`pointer-events-none absolute bottom-1.5 start-1.5 flex size-5 items-center justify-center rounded-full bg-[var(--color-bg-stage)]/60 ${file.position.match ? 'text-[var(--color-success)]' : 'text-[var(--color-warning)]'}`}
          >
            <icons.site size={12} weight="fill" />
            {!file.position.match && <span className="absolute -end-0.5 -top-0.5 size-2 rounded-full bg-[var(--color-warning)] ring-1 ring-[var(--color-bg-stage)]" />}
          </span>
        )}
        <span aria-hidden className="pointer-events-none absolute bottom-1.5 end-1.5 flex gap-1">
          {(file.family === 'video' || isNote) && file.media?.duration_ms !== undefined && <TileMark>{formatDuration(file.media.duration_ms)}</TileMark>}
        </span>
      </button>
      {/* The words behind the marks, for assistive tech. */}
      <span className="sr-only">
        {[file.fresh ? t('marks.new') : null, review ? t('marks.toReview') : null, file.position ? (file.position.match ? t('marks.positionMatch') : t('marks.positionOff')) : null].filter(Boolean).join(' · ')}
      </span>
      {quickActions && (
        <span data-quick className="absolute end-1 top-1 flex items-center gap-0.5 rounded-md bg-[var(--color-bg-stage)]/60 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
          {quickActions}
        </span>
      )}
      <span className="min-w-0 text-xs leading-4">
        <ShowsLine file={file} className="block truncate" />
        <span className="hidden truncate text-[var(--color-text-secondary)] @[8.5rem]:block" title={who ? t('meta.by', { name: who, date: whenFull }) : whenFull}>
          {who ? t('meta.by', { name: who, date: when }) : when}
        </span>
      </span>
    </div>
  );
}

/** The file's typed glyph in its FRAME — the library-row idiom, reused wherever a
 *  surface leads with the file: the shelf row, the peek's header, the full-screen
 *  line (`onStage` = the dark stage's own ink). A picture row shows its picture. */
export function FileGlyphTile({ file, size = 'md', onStage = false, thumb = true, className = '' }: { file: Pick<LibraryFile, 'family' | 'content_type' | 'thumb'>; size?: 'sm' | 'md'; onStage?: boolean; thumb?: boolean; className?: string }) {
  const box = size === 'sm' ? 'size-9' : 'size-10';
  const ground = onStage ? 'bg-[var(--color-on-stage)]/10 text-[var(--color-on-stage)]' : 'bg-[var(--color-bg-surface-alt)] text-[var(--color-text-secondary)]';
  return (
    <span className={`flex ${box} shrink-0 items-center justify-center overflow-hidden rounded-md ${ground} ${className}`}>
      {thumb && file.thumb ? <img src={file.thumb} alt="" className="size-full object-cover" /> : <FamilyGlyph family={file.family} contentType={file.content_type} size={size === 'sm' ? 22 : 24} />}
    </span>
  );
}

// ── a ROW (a document) ───────────────────────────────────────────────────────
// The library row idiom: the type glyph in a soft FRAME (a rounded tile on the
// alt surface — structure without colour), the name a step heavier, the facts
// a quiet line under it, the folder chip at the end. A file is a light entity,
// so the row is its identity line: the name is an
// `EntityRef` in OPEN mode (the one real control — keyboard, focus ring, the
// row's accessible name), STRETCHED over the row (its overlay makes the bare
// row select too, with no second handler — the chip and the quick acts sit
// above it as later positioned siblings); the chip is a `Tag` naming a THING
// (the band or the folder — never a Badge, which says status).
export interface FileRowProps {
  file: LibraryFile;
  /** The chip: the BAND for a site document under its folder heading, the FOLDER for a lane document (architect-ruled). Omit for none. */
  chipLabel?: string;
  selected?: boolean;
  onSelect?: (file: LibraryFile) => void;
  /** Visible quick acts (download) at the row's end. */
  quickActions?: ReactNode;
  /** Replaces the name line ("Version 2" on a versions row). */
  title?: string;
  /** Replaces the default subline (size · who · when). */
  subline?: ReactNode;
  /** Appended to the subline — a lane row names its product or asset here (an `EntityRef`). */
  context?: ReactNode;
  /** The chip as a DOOR (the folder filter); its accessible name says the act. */
  onChipClick?: () => void;
  chipAriaLabel?: string;
  /** A lane row (a product's / an asset's document) sits one step in. */
  indent?: boolean;
  timeZone?: string;
  className?: string;
}

export function FileRow({ file, chipLabel, selected = false, onSelect, quickActions, title, subline, context, onChipClick, chipAriaLabel, indent = false, timeZone, className = '' }: FileRowProps) {
  const { t } = useTranslation('files');
  const name = title ?? displayName(file);
  const when = formatDate(file.captured_at, 'date', timeZone ? { timeZone } : {}) ?? '';
  const who = file.uploaded_by ? t('meta.by', { name: file.uploaded_by.label, date: when }) : when;
  const defaultSubline = [formatBytes(file.size_bytes), file.provenance === 'catalog' ? t('row.fromCatalog') : who].filter(Boolean).join(' · ');
  return (
    <div
      data-file-id={file.id}
      aria-current={selected ? 'true' : undefined}
      className={
        `relative flex items-center gap-3 py-2.5 pe-3 ${indent ? 'ps-9' : 'ps-3'} ` +
        (onSelect ? 'hover:bg-[var(--color-bg-hover)] ' : '') +
        (selected ? 'bg-[var(--color-primary-light)] ' : '') +
        className
      }
    >
      <FileGlyphTile file={file} />
      <div className="min-w-0 flex-1">
        {onSelect ? (
          // Stretched over the row: the overlay is the bare-row click; the name stays the control.
          <span data-row-control className="contents">
            <EntityRef name={name} quiet onOpen={() => onSelect(file)} className="max-w-full text-sm after:absolute after:inset-0 after:content-['']" />
          </span>
        ) : (
          <span className="block max-w-full truncate text-sm font-medium text-[var(--color-text-primary)]">{name}</span>
        )}
        {/* The context (the product / asset the row belongs to) is the identifying
            fact — on a narrow row the size/provenance half gives way FIRST; the
            context only truncates once it alone overflows. */}
        <span className="flex min-w-0 items-baseline text-xs text-[var(--color-text-secondary)]">
          <span className="min-w-0 truncate">{subline ?? defaultSubline}</span>
          {context && (
            <span className="flex min-w-0 max-w-[75%] flex-none items-baseline">
              <span className="px-1" aria-hidden>
                ·
              </span>
              {context}
            </span>
          )}
        </span>
      </div>
      {chipLabel && (
        <Tag variant="neutral" className="relative" onClick={onChipClick} ariaLabel={onChipClick ? chipAriaLabel : undefined}>
          {file.restricted && <icons.locked size={12} aria-hidden />}
          {chipLabel}
        </Tag>
      )}
      {quickActions && (
        <span data-quick className="relative flex shrink-0 items-center gap-0.5">
          {quickActions}
        </span>
      )}
    </div>
  );
}

/** The roads a file may take (the shared rule): a document any, an app-uploaded photo only another site, a capture none. */
export function moveRoadsOf(file: Pick<LibraryFile, 'folder' | 'visit' | 'anchor'>): ('folder' | 'site' | 'asset')[] {
  return moveRoadsFor({ folder: file.folder, isCapture: file.anchor.kind === 'ticket', anchorKind: file.anchor.kind });
}
